import {
    Box,
    Card,
    Flex,
    Grid,
    HStack,
    Skeleton,
    SkeletonCircle,
    VStack,
} from "@chakra-ui/react";

const skeletonProps = {
    bg: "#DAE9FF",
    borderRadius: "md",
};

const cardProps = {
    bg: "white",
    border: "none",
    borderWidth: 0,
    boxShadow: "sm",
    borderRadius: "2xl",
};

const QuickActionCard = () => (
    <Card.Root {...cardProps}>
        <Card.Body
            p={{
                base: 4,
                md: 6,
            }}
        >
            <Flex
                justify="space-between"
                align="center"
            >
                <Box flex={1}>
                    <Skeleton
                        {...skeletonProps}
                        h="14px"
                        w="120px"
                        mb={3}
                    />

                    <Skeleton
                        {...skeletonProps}
                        h="12px"
                        w="90%"
                        mb={2}
                    />

                    <Skeleton
                        {...skeletonProps}
                        h="12px"
                        w="70%"
                    />
                </Box>

                <SkeletonCircle
                    bg="#DAE9FF"
                    size={{
                        base: "10",
                        md: "12",
                    }}
                />
            </Flex>
        </Card.Body>
    </Card.Root>
);

const KnowledgeCard = () => (
    <Card.Root {...cardProps}>
        <Card.Body
            p={{
                base: 4,
                md: 6,
            }}
        >
            <SkeletonCircle
                bg="#DAE9FF"
                size="12"
                mb={5}
            />

            <Skeleton
                {...skeletonProps}
                h="18px"
                w="150px"
                mb={3}
            />

            <Skeleton
                {...skeletonProps}
                h="12px"
                w="100%"
                mb={2}
            />

            <Skeleton
                {...skeletonProps}
                h="12px"
                w="80%"
            />
        </Card.Body>
    </Card.Root>
);

const TicketsTableSkeleton = () => (
    <Card.Root {...cardProps}>
        <Card.Body
            p={{
                base: 4,
                md: 6,
            }}
        >
            <Flex
                direction={{
                    base: "column",
                    md: "row",
                }}
                justify="space-between"
                align={{
                    base: "flex-start",
                    md: "center",
                }}
                gap={3}
                mb={6}
            >
                <Skeleton
                    {...skeletonProps}
                    h="24px"
                    w={{
                        base: "180px",
                        md: "220px",
                    }}
                />

                <Skeleton
                    {...skeletonProps}
                    h="38px"
                    w={{
                        base: "100%",
                        md: "220px",
                    }}
                />
            </Flex>

            <HStack
                display={{
                    base: "none",
                    md: "flex",
                }}
                gap={5}
                mb={5}
            >
                {Array.from({
                    length: 6,
                }).map((_, i) => (
                    <Skeleton
                        key={i}
                        {...skeletonProps}
                        h="12px"
                        flex={1}
                    />
                ))}
            </HStack>

            <VStack
                align="stretch"
                gap={4}
            >
                {Array.from({
                    length: 6,
                }).map((_, i) => (
                    <Flex
                        key={i}
                        direction={{
                            base: "column",
                            md: "row",
                        }}
                        justify="space-between"
                        align={{
                            base: "flex-start",
                            md: "center",
                        }}
                        gap={{
                            base: 4,
                            md: 0,
                        }}
                    >
                        <HStack
                            flex={2}
                            w={{
                                base: "100%",
                                md: "auto",
                            }}
                        >
                            <SkeletonCircle
                                bg="#DAE9FF"
                                size="10"
                            />

                            <Box flex={1}>
                                <Skeleton
                                    {...skeletonProps}
                                    h="12px"
                                    w="150px"
                                    mb={2}
                                />

                                <Skeleton
                                    {...skeletonProps}
                                    h="10px"
                                    w="90px"
                                />
                            </Box>
                        </HStack>

                        <Flex
                            w={{
                                base: "100%",
                                md: "auto",
                            }}
                            justify={{
                                base: "space-between",
                                md: "flex-end",
                            }}
                            align="center"
                            gap={4}
                            wrap="wrap"
                        >
                            <Skeleton
                                {...skeletonProps}
                                h="12px"
                                w="90px"
                            />

                            <Skeleton
                                {...skeletonProps}
                                h="12px"
                                w="90px"
                            />

                            <Skeleton
                                {...skeletonProps}
                                h="28px"
                                w="90px"
                                borderRadius="full"
                            />

                            <Skeleton
                                {...skeletonProps}
                                h="34px"
                                w="34px"
                                borderRadius="full"
                            />
                        </Flex>
                    </Flex>
                ))}
            </VStack>
        </Card.Body>
    </Card.Root>
);

export default function SupportSkeleton() {
    return (
        <>
            {/* Header */}

            <Box
                mb={{
                    base: 6,
                    md: 8,
                }}
            >
                <Skeleton
                    {...skeletonProps}
                    h="36px"
                    w={{
                        base: "220px",
                        md: "320px",
                    }}
                    mb={3}
                />

                <Skeleton
                    {...skeletonProps}
                    h="16px"
                    w={{
                        base: "100%",
                        md: "500px",
                    }}
                    mb={6}
                />

                <Skeleton
                    {...skeletonProps}
                    h="42px"
                    w={{
                        base: "160px",
                        md: "220px",
                    }}
                />
            </Box>

            {/* Quick Actions */}

            <Grid
                templateColumns={{
                    base: "1fr",
                    sm: "repeat(2,1fr)",
                    xl: "repeat(4,1fr)",
                }}
                gap={{
                    base: 4,
                    md: 6,
                }}
                mb={{
                    base: 6,
                    md: 8,
                }}
            >
                {Array.from({
                    length: 4,
                }).map((_, i) => (
                    <QuickActionCard key={i} />
                ))}
            </Grid>

            {/* Knowledge Base */}

            <Box mb={8}>
                <Skeleton
                    {...skeletonProps}
                    h="14px"
                    w="180px"
                    mb={5}
                />

                <Grid
                    templateColumns={{
                        base: "1fr",
                        sm: "repeat(2,1fr)",
                        xl: "repeat(4,1fr)",
                    }}
                    gap={{
                        base: 4,
                        md: 6,
                    }}
                >
                    {Array.from({
                        length: 8,
                    }).map((_, i) => (
                        <KnowledgeCard key={i} />
                    ))}
                </Grid>
            </Box>

            {/* Tickets */}

            <TicketsTableSkeleton />

            {/* Footer */}

            <Box
                mt={{
                    base: 6,
                    md: 8,
                }}
            >
                <Card.Root {...cardProps}>
                    <Card.Body
                        p={{
                            base: 4,
                            md: 6,
                        }}
                    >
                        <Flex
                            direction={{
                                base: "column",
                                md: "row",
                            }}
                            justify="space-between"
                            gap={4}
                        >
                            <Skeleton
                                {...skeletonProps}
                                h="12px"
                                w={{
                                    base: "100%",
                                    md: "220px",
                                }}
                            />

                            <HStack
                                gap={4}
                                flexWrap="wrap"
                            >
                                <Skeleton
                                    {...skeletonProps}
                                    h="12px"
                                    w="80px"
                                />

                                <Skeleton
                                    {...skeletonProps}
                                    h="12px"
                                    w="80px"
                                />

                                <Skeleton
                                    {...skeletonProps}
                                    h="12px"
                                    w="80px"
                                />
                            </HStack>
                        </Flex>
                    </Card.Body>
                </Card.Root>
            </Box>
        </>
    );
}